import GameState from "./game-state";
import NormalState from "./normal-state";
import DeadWaitState from "./dead-wait-state";
import WaitRespawnState from "./wait-respawn-state";
import { CanvasStatus } from "../types";
import { PacketWatcher } from "../game";
import { DownloadStatePacket, Packet } from "../../protocol/packets";
import { PacketTypes } from "../../protocol/types";
import Block from "../models/block";

export default class InitialState extends GameState implements PacketWatcher {
  private initialized: boolean = false;
  private transitioned: boolean = false;

  enter(): void {
    this.canvas.restart();

    const gameMode = this.game.getGameMode();
    const isSurvivorMode = gameMode === "survivor";

    if (isSurvivorMode) {
      const syncpoint = this.game.getSyncpoint?.();
      if (syncpoint === "waitForWinner" || syncpoint === "waitForRestart") {
        this.canvas.idle = 2;
        this.canvas.status = CanvasStatus.WAITFORWINNER;
      } else {
        this.canvas.idle = 1;
        this.canvas.status = CanvasStatus.PLAYING;
      }
    } else if (this.canvas.idle !== 2 && this.canvas.idle !== 3) {
      this.canvas.idle = 1;
    }

    if (this.canvas.status !== CanvasStatus.WAITFORWINNER) {
      this.canvas.status = CanvasStatus.PLAYING;
    }

    this.initialized = true;

    if (this.canvas.waitDownload) {
      console.log(
        `[InitialState.enter] Registering P_DOWNLOAD watcher, waitDownload=${this.canvas.waitDownload}, canvas.numPlayer=${this.canvas.numPlayer}`
      );
      this.game.addWatch(PacketTypes.DOWNLOAD_STATE, this);
      console.log(`[InitialState.enter] Watcher registered for P_DOWNLOAD`);
    } else {
      console.log(
        `[InitialState.enter] Not waiting for P_DOWNLOAD, waitDownload=${this.canvas.waitDownload}, canvas.numPlayer=${this.canvas.numPlayer}`
      );
    }
  }

  /**
   * Handle P_DOWNLOAD packet (PacketWatcher interface)
   * Reference: quadra/source/player.cc:877-918
   */
  onPacket(packet: Packet): void {
    if (packet.packetId === PacketTypes.DOWNLOAD_STATE) {
      console.log(
        `[InitialState.onPacket] Received P_DOWNLOAD, canvas.numPlayer=${this.canvas.numPlayer}, waitDownload=${this.canvas.waitDownload}`
      );
      this.processDownloadPacket(packet as DownloadStatePacket);
      this.game.removeWatch(PacketTypes.DOWNLOAD_STATE, this);
    }
  }

  /**
   * Process P_DOWNLOAD packet to sync remote canvas state
   * Reference: quadra/source/player.cc:887-916
   */
  private processDownloadPacket(packet: DownloadStatePacket): void {
    const canvas = this.canvas;

    for (let y = 0; y < 32; y++) {
      for (let x = 0; x < 10; x++) {
        canvas.blocks[y][x + 4] = packet.board[y][x];
        canvas.occupied[y][x + 4] = packet.occupied[y][x];
        canvas.blinded[y][x + 4] = packet.blinded[y][x];
      }
    }

    const previousSeed = canvas.rnd.get_seed();
    canvas.rnd.set_seed(packet.seed);
    const newSeed = canvas.rnd.get_seed();

    console.log(
      `[InitialState.processDownloadPacket] Processing P_DOWNLOAD for numPlayer=${canvas.numPlayer}: ` +
      `packet.seed=${packet.seed}, previousSeed=${previousSeed}, newSeed=${newSeed}, ` +
      `currentPiece=${packet.currentPiece}, nextPiece=${packet.nextPiece}, ` +
      `nextPiece2=${packet.nextPiece2}, nextPiece3=${packet.nextPiece3}`
    );

    if (packet.currentPiece !== 255) {
      canvas.bloc = new Block(packet.currentPiece, -1, 7, 10);
      canvas.next = new Block(packet.nextPiece, -1, 7, 10);
      canvas.next2 = new Block(packet.nextPiece2, -1, 7, 10);
      canvas.next3 = new Block(packet.nextPiece3, -1, 7, 10);
      console.log(
        `[InitialState.processDownloadPacket] Set pieces: bloc=${(canvas.bloc as any).quel || (canvas.bloc as any).type}, ` +
        `next=${(canvas.next as any).quel || (canvas.next as any).type}, ` +
        `next2=${(canvas.next2 as any).quel || (canvas.next2 as any).type}, ` +
        `next3=${(canvas.next3 as any).quel || (canvas.next3 as any).type}`
      );
    } else {
      console.log(
        `[InitialState.processDownloadPacket] No pieces in packet (currentPiece=255), ` +
        `canvas.bloc=${!!canvas.bloc}, canvas.next=${!!canvas.next}`
      );
    }

    canvas.bonus = packet.bonus;
    for (let i = 0; i < 20; i++) {
      if (packet.bonusLines[i]) {
        canvas.bon[i] = {
          x: packet.bonusLines[i].x,
          color: packet.bonusLines[i].color,
          blind_time: packet.bonusLines[i].blindTime,
          hole_pos: packet.bonusLines[i].holePos,
          final: packet.bonusLines[i].final,
        };
      }
    }

    canvas.idle = packet.idle;
    canvas.status = packet.canvasState as unknown as CanvasStatus;

    canvas.waitDownload = false;

    console.log(
      `[InitialState.processDownloadPacket] P_DOWNLOAD processed for numPlayer=${canvas.numPlayer}, ` +
      `seed=${newSeed}, waitDownload=false, idle=${canvas.idle}, status=${canvas.status}`
    );
  }

  update(frameTime: number): void {
    super.update(frameTime);

    if (this.transitioned) {
      return;
    }

    if (this.canvas.waitDownload) {
      return;
    }

    if (this.initialized) {
      this.transitioned = true;

      const gameMode = this.game.getGameMode();
      const isSurvivorMode = gameMode === "survivor";
      const savedIdle = this.canvas.idle;

      console.log(
        `[InitialState] Transitioning, idle=${savedIdle}, survivor=${isSurvivorMode}`
      );

      console.log(`[InitialState] About to push NormalState`);
      this.call(new NormalState(this.game));

      if (isSurvivorMode && savedIdle === 2) {
        if (this.game.player.isLocal()) {
          this.call(new DeadWaitState(this.game));
        } else {
          this.call(new WaitRespawnState(this.game));
        }
      }

      console.log(`[InitialState] Pushed states, now marking done`);

      this.markDone();
    }
  }

  toString(): string {
    return "Initial State";
  }
}
